import {
    ComposerAreaNode,
    ComposerAttributeNode,
    ComposerAttributeOptionNode,
    ComposerAttributeType,
    ComposerOptionActionNode,
    ComposerOptionActionsType,
    isComposerAreaLinkedToAttribute
} from './attributes/composerAttributesModels';

// CHE COSA DELLA SCENA TOCCA UNA RIGA DEL COMPOSER (utente, 2026-10-15, punto 9). Serve a due gesti
// del 3D: «Inquadra» guarda gli oggetti su cui agisce ciò che è scelto, e un clic su un oggetto dice
// quali righe lo usano. Le due domande sono la stessa letta nei due versi, e per questo stanno qui.
//
// UN RIFERIMENTO È UN ID O UN NOME, come lo scrivono le azioni: un oggetto (mostra, nascondi, cambia
// materiale) può essere una mesh o un nodo che ne contiene altre; un materiale ricolorato o ritoccato
// vale per tutte le mesh che lo portano.

export type ComposerSceneRefs = { objects: Set<string>; materials: Set<string> };

const splitIds = (value?: string | null) =>
    (value ?? '')
        .split(';')
        .map((id) => id.trim())
        .filter(Boolean);

const emptyRefs = (): ComposerSceneRefs => ({ objects: new Set(), materials: new Set() });

const OBJECT_ACTIONS = new Set<ComposerOptionActionsType>([
    ComposerOptionActionsType.SHOW_OBJECTS,
    ComposerOptionActionsType.HIDE_OBJECTS,
    ComposerOptionActionsType.SET_OBJECT_MATERIAL,
    ComposerOptionActionsType.SET_SUB_OBJECT_MATERIAL,
    ComposerOptionActionsType.SET_DESIGN_MATERIAL
]);

// Solo il materiale che l'azione CAMBIA (il selettore), non quello che mette: il valore di «cambia
// materiale» è il materiale nuovo, e le mesh che già lo portano non c'entrano con questa opzione.
const MATERIAL_ACTIONS = new Set<ComposerOptionActionsType>([ComposerOptionActionsType.SET_MATERIAL_COLOR, ComposerOptionActionsType.SET_MATERIAL_PARAMETER]);

const addActionRefs = (refs: ComposerSceneRefs, actions: ComposerOptionActionNode[]) => {
    actions.forEach((action) => {
        if (OBJECT_ACTIONS.has(action.type)) splitIds(action.selector).forEach((id) => refs.objects.add(id));
        if (MATERIAL_ACTIONS.has(action.type)) splitIds(action.selector).forEach((id) => refs.materials.add(id));
    });
    return refs;
};

export const getOptionSceneRefs = (option: ComposerAttributeOptionNode) => addActionRefs(emptyRefs(), option.actions ?? []);

export const getAreaSceneRefs = (area: ComposerAreaNode): ComposerSceneRefs => ({ objects: new Set(splitIds(area.objects)), materials: new Set() });

const merge = (target: ComposerSceneRefs, source: ComposerSceneRefs) => {
    source.objects.forEach((id) => target.objects.add(id));
    source.materials.forEach((id) => target.materials.add(id));
    return target;
};

const isAreaAttribute = (attribute: ComposerAttributeNode) =>
    attribute.type === ComposerAttributeType.Attribute && attribute.attributeType === MPlaza.AttributeType.CustomizableArea;

/** Un attributo tocca ciò che toccano le sue opzioni o le sue aree; un gruppo o un passo, ciò che toccano i suoi. */
const getAttributeSceneRefs = (attribute: ComposerAttributeNode, attributes: ComposerAttributeNode[], areas: ComposerAreaNode[]): ComposerSceneRefs => {
    const refs = emptyRefs();
    if (attribute.type === ComposerAttributeType.Attribute) {
        if (isAreaAttribute(attribute))
            areas.filter((area) => isComposerAreaLinkedToAttribute(area, attribute)).forEach((area) => merge(refs, getAreaSceneRefs(area)));
        else attribute.options.forEach((option) => merge(refs, getOptionSceneRefs(option)));
        return refs;
    }
    attributes.filter((child) => child.parentID === attribute.id).forEach((child) => merge(refs, getAttributeSceneRefs(child, attributes, areas)));
    return refs;
};

/** Ciò che tocca la selezione del Composer: le aree scelte, o le opzioni scelte, o gli attributi e i gruppi scelti. */
export const getSelectionSceneRefs = (state: {
    attributes: ComposerAttributeNode[];
    areas: ComposerAreaNode[];
    selectedAttributes: ComposerAttributeNode[] | null;
    selectedOptions: ComposerAttributeOptionNode[] | null;
    selectedAreas: ComposerAreaNode[] | null;
}): ComposerSceneRefs => {
    const refs = emptyRefs();
    if (state.selectedAreas?.length) return state.selectedAreas.reduce((all, area) => merge(all, getAreaSceneRefs(area)), refs);
    if (state.selectedOptions?.length) return state.selectedOptions.reduce((all, option) => merge(all, getOptionSceneRefs(option)), refs);
    (state.selectedAttributes ?? []).forEach((selected) => {
        const attribute = state.attributes.find((candidate) => candidate.id === selected.id) ?? selected;
        merge(refs, getAttributeSceneRefs(attribute, state.attributes, state.areas));
    });
    return refs;
};

export const hasSceneRefs = (refs: ComposerSceneRefs) => refs.objects.size > 0 || refs.materials.size > 0;

type SceneEditorLike = {
    scene?: BABYLON.Nullable<BABYLON.Scene>;
    isRealMesh?: (mesh: BABYLON.AbstractMesh) => boolean;
    getMeshesUsingMaterial?: (materialID: string) => BABYLON.AbstractMesh[];
};

const isDrawn = (sceneEditor: SceneEditorLike, mesh: BABYLON.AbstractMesh) =>
    (sceneEditor.isRealMesh?.(mesh) ?? true) && mesh.isEnabled() && mesh.getTotalVertices() > 0;

/** Le mesh disegnate che i riferimenti indicano: il nodo stesso se è una mesh, e le mesh che contiene. */
export const resolveSceneRefs = (sceneEditor: SceneEditorLike | null | undefined, refs: ComposerSceneRefs): BABYLON.AbstractMesh[] => {
    const scene = sceneEditor?.scene;
    if (!sceneEditor || !scene) return [];
    const meshes = new Map<number, BABYLON.AbstractMesh>();
    const add = (mesh: BABYLON.AbstractMesh) => {
        if (isDrawn(sceneEditor, mesh)) meshes.set(mesh.uniqueId, mesh);
    };
    refs.objects.forEach((ref) => {
        const node = scene.getNodeById(ref) ?? scene.getNodeByName(ref);
        if (!node) return;
        if ('getTotalVertices' in node) add(node as BABYLON.AbstractMesh);
        node.getChildMeshes(false).forEach(add);
    });
    refs.materials.forEach((id) => (sceneEditor.getMeshesUsingMaterial?.(id) ?? []).forEach(add));
    return Array.from(meshes.values());
};

/** Una mesh è toccata da dei riferimenti se loro nominano lei, uno dei nodi che la contengono o il suo materiale. */
const meshMatches = (mesh: BABYLON.AbstractMesh, refs: ComposerSceneRefs) => {
    let node: BABYLON.Node | null = mesh;
    while (node) {
        if (refs.objects.has(node.id) || refs.objects.has(node.name)) return true;
        node = node.parent;
    }
    const material = mesh.material;
    if (!material) return false;
    if (refs.materials.has(material.id) || refs.materials.has(material.name)) return true;
    const subMaterials = (material as BABYLON.MultiMaterial).subMaterials ?? [];
    return subMaterials.some((sub) => !!sub && (refs.materials.has(sub.id) || refs.materials.has(sub.name)));
};

export type ComposerMeshUser =
    | { kind: 'option'; id: string; owner: ComposerAttributeNode; option: ComposerAttributeOptionNode; label: string }
    | { kind: 'area'; id: string; owner: ComposerAttributeNode; area: ComposerAreaNode; label: string };

/** Le opzioni e le aree che usano una mesh, con l'attributo che le contiene: «Attributo › Opzione». */
export const getMeshUsers = (mesh: BABYLON.AbstractMesh, attributes: ComposerAttributeNode[], areas: ComposerAreaNode[]): ComposerMeshUser[] => {
    const users: ComposerMeshUser[] = [];
    attributes
        .filter((attribute) => attribute.type === ComposerAttributeType.Attribute)
        .forEach((attribute) => {
            if (isAreaAttribute(attribute)) {
                areas
                    .filter((area) => isComposerAreaLinkedToAttribute(area, attribute) && meshMatches(mesh, getAreaSceneRefs(area)))
                    .forEach((area) => users.push({ kind: 'area', id: area.id, owner: attribute, area, label: `${attribute.name} › ${area.name}` }));
                return;
            }
            attribute.options
                .filter((option) => meshMatches(mesh, getOptionSceneRefs(option)))
                .forEach((option) => users.push({ kind: 'option', id: option.id, owner: attribute, option, label: `${attribute.name} › ${option.name}` }));
        });
    return users;
};
